// Trigger Management Types
export interface WorkflowTriggerDTO {
  triggerId: string;
  workflowId: string;
  triggerName?: string;
  description?: string;
  httpMethod: string;
  rateLimit?: number;
  active: boolean;
  webhookUrl: string;
  createdAt: string;
  updatedAt: string;
  triggerType?: string;
  triggerSource?: string;
  formPath?: string;
  formUrl?: string;
  workflowName?: string;
  workflowLifecycleState?: string;
}

export interface ApiTriggerResponseDTO {
  triggerId: string;
  triggeredWorkflowCount: number;
  message: string;
  timestamp: string;
}

export interface TriggerConfigDTO {
  webhookUrlPattern: string;
  supportedMethods: string[];
  triggerIdLength: number;
  maxRequestSize: string;
  defaultRateLimit: string;
  usage: string;
}

// Common types and interfaces for the API modules

// UI Types
export interface NodeItem {
  type: string;
  label: string;
  description: string;
  icon: string;
}

export interface WorkflowNodes {
  triggers: NodeItem[];
  actions: NodeItem[];
  integrations: NodeItem[];
  logic: NodeItem[];
}

// Workflow Types
export interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  animated?: boolean;
  style?: any;
  label?: string;
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  failOnError?: boolean;
  projectId?: string;
  tenantId?: string;
  workspaceId?: string;
  version?: string;  // "1.0" (legacy) or "2.0" (plugin-based)
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  meta?: Record<string, any>;
}

export interface WorkflowPayload {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  developerMode?: boolean;
}

export interface LoadedWorkflow {
  id: string;
  name?: string;
  description?: string;
  failOnError?: boolean;
  projectId?: string;
  tenantId?: string;
  workspaceId?: string;
  version?: string;  // "1.0" (legacy) or "2.0" (plugin-based)
  payload: WorkflowPayload;
  meta?: Record<string, any>;
  executionCount?: number;
  createdAt?: string;
  createdBy?: string;
  updatedBy?: string;
  workflowStatus?: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  // EF-251: Lifecycle fields
  lifecycleState?: WorkflowLifecycleState;
  logicalWorkflowId?: string;
  revisionNumber?: number;
  publishedAt?: string;
}

// Policy Types
export interface Policy {
  id: string;
  name: string;
  trigger: { type: string };
  conditions: Array<{ field: string; operator: string; value: any }>;
  actions: Array<{ type: string; params?: Record<string, any> }>;
}

// Execution Log Types
export interface WorkflowExecutionLogDTO {
  id: string;
  workflowId: string;
  /** EF-255: Logical workflow ID for cross-version aggregation */
  logicalWorkflowId?: string;
  workflowName: string;
  /** EF-255: Revision number of the version that ran (for version badge) */
  workflowRevisionNumber?: string;
  correlationId: string;
  status: string;
  inputPayload?: string;
  errorMessage?: string;
  durationMs?: number;
  createdAt: string;
  lastUpdated: string;
  startTime: string;
  endTime?: string;
  developerMode?: boolean;
  artifactCount?: number;
}

export interface NodeExecutionLogDTO {
  id: string;
  nodeId: string;
  nodeName?: string;
  nodeIdentifier?: string;
  nodeType: string;
  status: string;
  inputData?: string;
  outputData?: string;
  errorMessage?: string;
  durationMs?: number;
  createdAt: string;
  lastUpdated: string;
  startTime: string;
  endTime?: string;
  hasArtifacts?: boolean;
}

export interface ExecutionArtifactDTO {
  id: number;
  workflowExecutionId: string;
  nodeId: string;
  nodeName?: string;
  nodeIdentifier?: string;
  storageKey: string;
  fileName: string;
  contentType: string;
  contentSize: number;
  storageType: string;
  persist: boolean;
  createdAt: string;
}

export interface ArtifactSignedUrlResponse {
  signedUrl: string;
  expiresIn: number;
  contentType: string;
}

export interface ExecutionLogsResponse {
  contents: WorkflowExecutionLogDTO[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface GetExecutionLogsParams {
  page?: number;
  size?: number;
  status?: string;
  search?: string;
  /** Legacy – physical version ID filter (CEL-based) */
  workflowId?: string;
  executionId?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** EF-255: Show all executions for this logical workflow (across versions) */
  logicalWorkflowId?: string;
  /** EF-255: Show executions for this specific physical version only */
  versionId?: string;
}

// Re-export common API types for convenience
export type { PagedResponse, PaginatedResponse, PaginationParams, ApiErrorResponse } from '@/types/api';

export interface WorkflowExecutionResult {
  executionId: string;
  status: string;
}

// EF-251: Workflow lifecycle types

export type WorkflowLifecycleState = 'DRAFT' | 'LIVE' | 'HISTORICAL';

export interface WorkflowVersionSummaryDTO {
  versionId: string;
  logicalWorkflowId: string;
  revisionNumber: number;
  lifecycleState: WorkflowLifecycleState;
  publishedAt?: string;
  createdAt?: string;
  name?: string;
  nodesCount?: number;
  edgesCount?: number;
  /** EF-255: Execution count for this specific version */
  executionCount?: number;
}

export interface PublishDraftResponse {
  ok: boolean;
  liveVersionId: string;
  revisionNumber: number;
  logicalWorkflowId: string;
}

// API Configuration
export const getApiBase = (): string => {
  return (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8080';
};

export const API_ENDPOINTS = {
  // Workflow service base
  WORKFLOW_BASE: getApiBase() + '/workflow',
  
  // Workflow service endpoints
  WORKFLOW_NODES: getApiBase() + '/workflow/api/v1/flow-ui',
  WORKFLOW: getApiBase() + '/workflow/api/v1/workflow',
  WORKFLOW_V2: getApiBase() + '/workflow/api/v2/workflow',
  POLICIES: getApiBase() + '/workflow/api/v1/policies',
  EXECUTION_LOGS: getApiBase() + '/workflow/api/v1/workflow/executions/logs',
  NODE_EXECUTION_LOGS: getApiBase() + '/workflow/api/v1/workflow/executions/logs',
  TRIGGERS: getApiBase() + '/workflow/api/v1/triggers',
  PLUGINS: getApiBase() + '/workflow/api/v1/plugins',
  ADMIN_PLUGINS: getApiBase() + '/workflow/api/v1/admin/plugins',
  TENANT_PLUGINS: getApiBase() + '/workflow/api/v1/admin/plugins/tenants',
  WORKFLOW_EXECUTOR: getApiBase() + '/workflow-executor',
  // EF-251: Lifecycle endpoints
  WORKFLOW_LIFECYCLE: getApiBase() + '/workflow/api/v2/workflows',
  // EF-252: Org entitlement checks (generic — usable across all CMS features)
  ORG_ENTITLEMENTS: getApiBase() + '/workflow/api/v2/org',
  // Dashboard summary endpoint
  DASHBOARD: getApiBase() + '/workflow/api/v2/dashboard',
  TEMPLATES: getApiBase() + '/workflow/api/v1/templates',
  FORMS_V2: getApiBase() + '/workflow/api/v2/forms',

  // Billing service endpoints
  BILLING: getApiBase() + '/billing/api/v1',
  
  // Identity service endpoints
  IDENTITY: getApiBase() + '/identity/api/v1',
} as const;